import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../hooks/useConfirm';
import Layout from '../../components/Layout';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';
import Input from '../../components/ui/Input';
import { formConfigService } from '../../services/form-config.service';
import {
  FormConfig,
  FormFieldDef,
  FormStepDef,
  FormType,
  InputType,
  FORM_TYPE_LABELS,
  FormConfigSectionOptions,
} from '../../interfaces/form-config.interface';
import { OPTION_BASED, ALL_INPUT_TYPES } from './components/form-config.utils';
import { getErrorMsg } from '../../utils/error';
import StepTabs from './components/StepTabs';
import { FieldTable } from './components/FieldTable';
import { FieldDrawer } from './components/FieldDrawer';
import { SectionMapTab } from './components/SectionMapTab';
import { SectionOptionsTab } from './components/SectionOptionsTab';
import { ConfigSettingsTab } from './components/ConfigSettingsTab';
import { GroupPicker } from './components/GroupPicker';
import { GroupsTab } from './components/GroupsTab';
import { FormGroupDef } from '../../interfaces/form-config.interface';

// ── AddFieldDialog ─────────────────────────────────────────────────────────

interface AddFieldForm {
  key: string;
  label: string;
  labelZh: string;
  inputType: InputType;
  step: number;
  group: string;
  required: boolean;
}

interface AddFieldDialogProps {
  open: boolean;
  allSteps: number[];
  allGroups: string[];
  defaultStep: number;
  existingKeys: Set<string>;
  onClose: () => void;
  onAdd: (field: FormFieldDef) => void;
}

const AddFieldDialog = ({ open, allSteps, allGroups, defaultStep, existingKeys, onClose, onAdd }: AddFieldDialogProps) => {
  const [form, setForm] = useState<AddFieldForm>({
    key: '', label: '', labelZh: '', inputType: InputType.TEXTBOX,
    step: defaultStep, group: '', required: false,
  });
  const [keyError, setKeyError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ key: '', label: '', labelZh: '', inputType: InputType.TEXTBOX, step: defaultStep, group: '', required: false });
      setKeyError('');
    }
  }, [open, defaultStep]);

  const setF = (patch: Partial<AddFieldForm>) => setForm((p) => ({ ...p, ...patch }));

  const handleKeyChange = (val: string) => {
    const cleaned = val.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setF({ key: cleaned });
    setKeyError(existingKeys.has(cleaned) ? 'Key already exists' : '');
  };

  const canSubmit = form.key && form.label && !keyError;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const newField: FormFieldDef = {
      key: form.key,
      label: form.label,
      inputType: form.inputType,
      step: form.step,
      order: 999,
      group: form.group || undefined,
      required: form.required,
      visible: true,
      isFilterable: false,
      isSortable: false,
      isSearchable: false,
      isListFilter: false,
      allowMultiple: false,
      layoutFixed: false,
      detailFixed: false,
      translations: {
        en: { label: form.label },
        'zh-Hant': { label: form.labelZh || form.label },
      },
      ...(OPTION_BASED.has(form.inputType)
        ? { options: [{ value: '', label: '', translations: { en: '', 'zh-Hant': '' } }] }
        : {}),
    };
    onAdd(newField);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Add New Field">
      <div className="space-y-4 min-w-[380px]">
        <div>
          <Input
            label="Field Key (snake_case)"
            placeholder="e.g. hull_material"
            value={form.key}
            onChange={(e) => handleKeyChange(e.target.value)}
          />
          {keyError && <p className="mt-1 text-xs text-red-500">{keyError}</p>}
          <p className="mt-1 text-xs text-gray-400">Unique identifier. Cannot be changed after creation.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Label (EN)" value={form.label} onChange={(e) => setF({ label: e.target.value })} />
          <Input label="Label (ZH)" placeholder="Same as EN if blank" value={form.labelZh} onChange={(e) => setF({ labelZh: e.target.value })} />
        </div>

        <div className={allSteps.length > 1 ? 'grid grid-cols-2 gap-3' : ''}>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Input Type</label>
            <select
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={form.inputType}
              onChange={(e) => setF({ inputType: e.target.value as InputType })}
            >
              {ALL_INPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {allSteps.length > 1 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Step</label>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={form.step}
                onChange={(e) => setF({ step: Number(e.target.value) })}
              >
                {allSteps.map((s) => <option key={s} value={s}>Step {s}</option>)}
              </select>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Group (optional)</label>
          <GroupPicker value={form.group || undefined} existingGroups={allGroups} onChange={(g) => setF({ group: g ?? '' })} />
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="rounded" checked={form.required} onChange={(e) => setF({ required: e.target.checked })} />
          <span>Required field</span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>Add Field</Button>
        </div>
      </div>
    </Dialog>
  );
};

// ── Main editor ───────────────────────────────────────────────────────────

const FormConfigEditor = () => {
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();
  const { formType } = useParams<{ formType: string }>();
  const navigate = useNavigate();

  // ── Core data ────────────────────────────────────────────────────────────
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sectionOptions, setSectionOptions] = useState<FormConfigSectionOptions>({ listing: [], detail: [] });
  const [localSteps, setLocalSteps] = useState<FormStepDef[]>([]);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [activeStep, setActiveStep] = useState<number | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'fields' | 'groups' | 'section-map' | 'section-options' | 'settings'>('fields');
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);

  // ── Dirty tracking ────────────────────────────────────────────────────────
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [orderDirty, setOrderDirty] = useState(false);
  const [stepsDirty, setStepsDirty] = useState(false);
  const [sectionMapDirty, setSectionMapDirty] = useState(false);

  // ── Saving flags ──────────────────────────────────────────────────────────
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savingSteps, setSavingSteps] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingSectionMap, setSavingSectionMap] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Config settings local state ───────────────────────────────────────────
  const [metaIcon, setMetaIcon] = useState('');
  const [metaRemark, setMetaRemark] = useState('');
  const [metaListingCategoryId, setMetaListingCategoryId] = useState<string | null>(null);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [addFieldDefaultStep, setAddFieldDefaultStep] = useState(1);
  const [globalMsg, setGlobalMsg] = useState('');

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    if (!formType) return;
    setLoading(true);
    formConfigService.getByType(formType as FormType)
      .then((cfg) => {
        setConfig(cfg);
        setLocalSteps(cfg.steps ?? []);
        setOrderDirty(false);
        setStepsDirty(false);
        setMetaIcon(cfg.icon ?? '');
        setMetaRemark(cfg.remark ?? '');
        setMetaListingCategoryId(cfg.listingCategoryId ?? null);
        setSectionMapDirty(false);
      })
      .catch((e) => setError(e?.message || 'Failed to load config'))
      .finally(() => setLoading(false));
  }, [formType]);

  useEffect(() => {
    load();
    formConfigService.getSectionOptions()
      .then(setSectionOptions)
      .catch(() => {}); // non-critical
  }, [load]);

  // ── Derived values ────────────────────────────────────────────────────────
  const sortedStepDefs: FormStepDef[] = [...localSteps].sort((a, b) => a.sortOrder - b.sortOrder);
  const stepNumbers = sortedStepDefs.map((s) => s.stepNumber);
  const allStepNumbers = stepNumbers.length > 0
    ? stepNumbers
    : config ? Array.from(new Set(config.fields.map((f) => f.step))).sort((a, b) => a - b) : [];
  const maxStep = allStepNumbers.length > 0 ? Math.max(...allStepNumbers) : 1;

  const fieldCounts: Record<number, number> = {};
  if (config) {
    for (const f of config.fields) {
      fieldCounts[f.step] = (fieldCounts[f.step] ?? 0) + 1;
    }
  }

  const allGroups = Array.from(new Set(
    (config?.fields ?? []).filter((f) => f.group).map((f) => f.group!)
  ));

  const existingKeys = config ? new Set(config.fields.map((f) => f.key)) : new Set<string>();
  const addFieldAllSteps = allStepNumbers.length > 0 ? allStepNumbers : [1];

  const visibleFields = config
    ? (activeStep === 'all'
        ? [...config.fields].sort((a, b) => a.step - b.step || a.order - b.order)
        : config.fields.filter((f) => f.step === activeStep).sort((a, b) => a.order - b.order))
    : [];

  const selectedField = config?.fields.find((f) => f.key === selectedFieldKey) ?? null;

  const label = formType ? (FORM_TYPE_LABELS[formType as FormType] ?? formType) : '';

  // ── Helpers ───────────────────────────────────────────────────────────────
  const flash = (msg: string) => { setGlobalMsg(msg); setTimeout(() => setGlobalMsg(''), 3000); };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSaveField = async (fieldKey: string, patch: Partial<FormFieldDef>) => {
    const fieldId = config?.fields.find((f) => f.key === fieldKey)?.id;
    if (!fieldId) return;
    setSavingKey(fieldKey);
    try {
      const updated = await formConfigService.patchField(fieldId, patch);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setDirtyKeys((prev) => { const s = new Set(prev); s.delete(fieldKey); return s; });
      if (patch.step !== undefined && patch.step !== activeStep) setActiveStep(patch.step);
      flash('Field saved');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Save failed'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleSetFieldStatus = async (fieldKey: string, isActive: boolean) => {
    const fieldId = config?.fields.find((f) => f.key === fieldKey)?.id;
    if (!fieldId) return;
    try {
      const updated = await formConfigService.setFieldStatus(fieldId, isActive);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      flash(isActive ? `"${fieldKey}" activated` : `"${fieldKey}" deactivated`);
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Status update failed'));
    }
  };

  const handleDeleteField = async (fieldKey: string) => {
    const fieldId = config?.fields.find((f) => f.key === fieldKey)?.id;
    if (!fieldId) return;
    try {
      const updated = await formConfigService.deleteField(fieldId);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      if (selectedFieldKey === fieldKey) setSelectedFieldKey(null);
      flash(`Field "${fieldKey}" deleted`);
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Delete field failed'));
    }
  };

  const handleReorder = (reorderedFields: FormFieldDef[]) => {
    if (!config) return;
    if (activeStep === 'all') {
      setConfig({ ...config, fields: reorderedFields });
    } else {
      const other = config.fields.filter((f) => f.step !== activeStep);
      setConfig({ ...config, fields: [...other, ...reorderedFields] });
    }
    setOrderDirty(true);
  };

  const handleSaveOrder = async () => {
    if (!config) return;
    if (!await confirm(t('confirm_save_order'))) return;
    setSavingOrder(true);
    try {
      const updated = await formConfigService.updateFields(config.id, config.fields);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setOrderDirty(false);
      flash('Field order saved');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Save order failed'));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleStepReorder = (reordered: FormStepDef[]) => {
    setLocalSteps(reordered);
    setStepsDirty(true);
  };

  const handleStepRename = async (step: FormStepDef, translations: Record<string, string>) => {
    if (!config) return;
    try {
      const updated = await formConfigService.reorderSteps(
        config.id,
        localSteps.map((s) => ({
          stepNumber: s.stepNumber,
          translations: s.stepNumber === step.stepNumber ? translations : s.translations,
        })),
      );
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      flash('Step renamed');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Rename failed'));
    }
  };

  const handleSaveSteps = async () => {
    if (!config) return;
    if (!await confirm(t('confirm_save_steps'))) return;
    setSavingSteps(true);
    try {
      const updated = await formConfigService.reorderSteps(
        config.id,
        localSteps.map((s) => ({ stepNumber: s.stepNumber, translations: s.translations })),
      );
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setStepsDirty(false);
      flash('Step order saved');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Save steps failed'));
    } finally {
      setSavingSteps(false);
    }
  };

  const handleAddField = async (newField: FormFieldDef) => {
    if (!config) return;
    const stepFields = config.fields.filter((f) => f.step === newField.step);
    newField.order = stepFields.length > 0 ? Math.max(...stepFields.map((f) => f.order)) + 1 : 1;
    try {
      const updated = await formConfigService.updateFields(config.id, [...config.fields, newField]);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setAddFieldOpen(false);
      setActiveStep(newField.step);
      flash(`Field "${newField.key}" added to Step ${newField.step}`);
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Add field failed'));
    }
  };

  const handleAddStep = async () => {
    if (!config) return;
    const newStepNumber = maxStep + 1;
    const newSteps = [
      ...localSteps,
      { id: String(newStepNumber), stepNumber: newStepNumber, sortOrder: localSteps.length, translations: { en: `Step ${newStepNumber}` } },
    ];
    try {
      const updated = await formConfigService.reorderSteps(
        config.id,
        newSteps.map((s) => ({ stepNumber: s.stepNumber, translations: s.translations })),
      );
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setActiveStep(newStepNumber);
      flash(`Step ${newStepNumber} added`);
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Add step failed'));
    }
  };

  const handleSaveMeta = async () => {
    if (!config) return;
    if (!await confirm(t('confirm_save_meta'))) return;
    setSavingMeta(true);
    try {
      const updated = await formConfigService.patchMeta(config.id, {
        icon: metaIcon || undefined,
        remark: metaRemark || undefined,
        listingCategoryId: metaListingCategoryId,
      });
      setConfig(updated);
      flash('Config meta saved');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Save meta failed'));
    } finally {
      setSavingMeta(false);
    }
  };

  const handleSaveSectionMap = async () => {
    if (!config) return;
    if (!await confirm(t('confirm_save_section_map'))) return;
    setSavingSectionMap(true);
    try {
      const updated = await formConfigService.updateFields(config.id, config.fields);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setSectionMapDirty(false);
      flash('Section mapping saved');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Save section mapping failed'));
    } finally {
      setSavingSectionMap(false);
    }
  };

  const handleSectionChange = (
    fieldKey: string,
    col: 'listingSection' | 'detailSection',
    value: string | null,
  ) => {
    if (!config) return;
    setConfig({
      ...config,
      fields: config.fields.map((f) => f.key === fieldKey ? { ...f, [col]: value } : f),
    });
    setSectionMapDirty(true);
  };

  const handleAddSectionOption = async (type: 'listing' | 'detail', value: string): Promise<void> => {
    const created = await formConfigService.createSectionOption(type, value);
    setSectionOptions((prev) => ({
      ...prev,
      [type]: [...prev[type], created].sort((a, b) => a.sortOrder - b.sortOrder),
    }));
  };

  const handleToggleStatus = async () => {
    if (!config) return;
    setSavingStatus(true);
    setStatusDialogOpen(false);
    try {
      const updated = await formConfigService.setStatus(config.id, !config.isActive);
      setConfig(updated);
      flash(updated.isActive ? 'Config activated' : 'Config deactivated');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Status update failed'));
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!config) return;
    setDeleting(true);
    try {
      await formConfigService.deleteConfig(config.id);
      navigate('/form-configs');
    } catch (e: unknown) {
      setDeleteDialogOpen(false);
      setDeleting(false);
      setGlobalMsg(getErrorMsg(e, 'Delete failed'));
    }
  };

  const handleGroupsChange = (groups: FormGroupDef[]) => {
    if (!config) return;
    setConfig({ ...config, fieldGroups: groups });
  };

  const handleReset = async () => {
    if (!config) return;
    setResetDialogOpen(false);
    try {
      const updated = await formConfigService.reset(config.id);
      setConfig(updated);
      setLocalSteps(updated.steps ?? []);
      setDirtyKeys(new Set());
      setOrderDirty(false);
      setStepsDirty(false);
      flash('Config reset to defaults');
    } catch (e: unknown) {
      setGlobalMsg(getErrorMsg(e, 'Reset failed'));
    }
  };

  // ── Loading / Error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout title={`${label} — ${t('form_config_editor')}`} showBackButton>
        <div className="flex-1 flex items-center justify-center text-gray-400">{t('loading')}</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={`${label} — ${t('form_config_editor')}`} showBackButton>
        <div className="flex-1 flex items-center justify-center text-red-500">{error}</div>
      </Layout>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout title={`${label} — ${t('form_config_editor')}`} showBackButton>
      <div className="flex flex-col flex-1 overflow-hidden">

        {/* ── Zone 1: Meta strip ── */}
        <div className="flex-shrink-0 bg-gray-900 dark:bg-gray-950 text-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {config?.icon && <img src={config.icon} alt="" className="w-6 h-6 object-contain rounded shrink-0" />}
            <span className="font-semibold text-lg truncate">{label}</span>
            {config && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${
                config.isActive
                  ? 'bg-green-700/60 text-green-300'
                  : 'bg-gray-700 text-gray-400'
              }`}>
                {config.isActive ? 'Active' : 'Inactive'}
              </span>
            )}
            {config && (
              <span className="text-xs text-gray-500 shrink-0">v{config.version}</span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {orderDirty && (
              <Button size="sm" variant="primary" onClick={handleSaveOrder} disabled={savingOrder}>
                {savingOrder ? 'Saving…' : 'Save Order'}
              </Button>
            )}
            {stepsDirty && (
              <Button size="sm" variant="secondary" onClick={handleSaveSteps} disabled={savingSteps}>
                {savingSteps ? 'Saving…' : 'Save Steps'}
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setAddFieldDefaultStep(activeStep === 'all' ? (allStepNumbers[0] ?? 1) : activeStep); setAddFieldOpen(true); }}
            >
              + Add Field
            </Button>

            {/* Overflow menu */}
            <div className="relative group">
              <button
                type="button"
                className="px-2 py-1 rounded text-gray-300 hover:text-white hover:bg-gray-700 text-lg leading-none transition-colors"
                title="More options"
              >
                ⋯
              </button>
              <div className="hidden group-hover:flex absolute right-0 top-full mt-1 z-50 flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden min-w-[170px]">
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  disabled={savingStatus}
                  onClick={() => setStatusDialogOpen(true)}
                >
                  {config?.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => setResetDialogOpen(true)}
                >
                  Reset to defaults
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  onClick={() => { setDeleteConfirmText(''); setDeleteDialogOpen(true); }}
                >
                  Delete config
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Zone 2: Step tabs ── */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 pt-2 pb-0">
          {config && (
            <StepTabs
              steps={sortedStepDefs.length > 0
                ? sortedStepDefs
                : allStepNumbers.map((n, i) => ({ id: String(n), stepNumber: n, sortOrder: i, translations: { en: `Step ${n}` } }))}
              activeStep={activeStep}
              fieldCounts={fieldCounts}
              onSelect={setActiveStep}
              onReorder={handleStepReorder}
              onRename={handleStepRename}
              onAddStep={handleAddStep}
            />
          )}
        </div>

        {/* ── Zone 3: View tabs ── */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 flex gap-0">
          {(['fields', 'groups', 'section-map', 'section-options', 'settings'] as const).map((tab) => {
            const tabLabels = { fields: 'Fields', groups: 'Groups', 'section-map': 'Section Map', 'section-options': 'Section Options', settings: 'Config Settings' };
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  isActive
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
                onClick={() => {
                  setActiveTab(tab);
                  if (tab !== 'fields') setSelectedFieldKey(null);
                }}
              >
                {tabLabels[tab]}
              </button>
            );
          })}
        </div>

        {/* ── Global message ── */}
        {globalMsg && (
          <div className={`flex-shrink-0 mx-4 mt-2 p-2 rounded text-sm ${
            globalMsg.toLowerCase().includes('fail') || globalMsg.toLowerCase().includes('error')
              ? 'bg-red-50 text-red-600 border border-red-100'
              : 'bg-green-50 text-green-700 border border-green-100'
          }`}>{globalMsg}</div>
        )}

        {/* ── Main content ── */}
        {config && config.fields.length === 0 && activeTab === 'fields' ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-sm space-y-4 p-8 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="text-5xl">📋</div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">No fields configured</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  This config has no fields yet. Generate the standard field set from the default template,
                  or add fields manually.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={() => setResetDialogOpen(true)}>Generate Default Fields</Button>
                <Button
                  variant="secondary"
                  onClick={() => { setAddFieldDefaultStep(1); setAddFieldOpen(true); }}
                >
                  Add Field Manually
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-row flex-1 overflow-hidden">
            {/* Fields tab */}
            {activeTab === 'fields' && config && (
              <>
                <div className="flex-1 overflow-y-auto p-4">
                  <FieldTable
                    fields={visibleFields}
                    selectedKey={selectedFieldKey}
                    dirtyKeys={dirtyKeys}
                    onSelectField={setSelectedFieldKey}
                    onReorder={handleReorder}
                  />
                </div>
                {selectedFieldKey && (
                  <div className="w-[480px] flex-shrink-0 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
                    <FieldDrawer
                      field={selectedField}
                      saving={savingKey === selectedFieldKey}
                      allSteps={sortedStepDefs.length > 0
                        ? sortedStepDefs
                        : allStepNumbers.map((n, i) => ({ id: String(n), stepNumber: n, sortOrder: i, translations: { en: `Step ${n}` } }))}
                      allGroups={allGroups}
                      allFieldKeys={(config?.fields ?? []).map((f) => f.key)}
                      listingSections={sectionOptions.listing.map((o) => o.value)}
                      detailSections={sectionOptions.detail.map((o) => o.value)}
                      onSave={handleSaveField}
                      onSetStatus={handleSetFieldStatus}
                      onDelete={handleDeleteField}
                      onClose={() => setSelectedFieldKey(null)}
                      onDirtyChange={(isDirty) => {
                        if (!selectedFieldKey) return;
                        setDirtyKeys((prev) => {
                          const s = new Set(prev);
                          if (isDirty) s.add(selectedFieldKey);
                          else s.delete(selectedFieldKey);
                          return s;
                        });
                      }}
                    />
                  </div>
                )}
              </>
            )}

            {/* Groups tab */}
            {activeTab === 'groups' && config && (
              <div className="flex-1 overflow-y-auto p-4">
                <GroupsTab
                  configId={config.id}
                  fields={config.fields}
                  fieldGroups={config.fieldGroups ?? []}
                  onGroupsChange={handleGroupsChange}
                  onFlash={flash}
                />
              </div>
            )}

            {/* Section Map tab */}
            {activeTab === 'section-map' && config && (
              <div className="flex-1 overflow-y-auto p-4">
                <SectionMapTab
                  fields={config.fields}
                  sectionOptions={sectionOptions}
                  dirty={sectionMapDirty}
                  saving={savingSectionMap}
                  onSectionChange={handleSectionChange}
                  onSave={handleSaveSectionMap}
                />
              </div>
            )}

            {/* Section Options tab */}
            {activeTab === 'section-options' && (
              <div className="flex-1 overflow-y-auto p-4">
                <SectionOptionsTab
                  sectionOptions={sectionOptions}
                  onAddSectionOption={handleAddSectionOption}
                />
              </div>
            )}

            {/* Config Settings tab */}
            {activeTab === 'settings' && (
              <div className="flex-1 overflow-y-auto p-4">
                <ConfigSettingsTab
                  icon={metaIcon}
                  remark={metaRemark}
                  listingCategoryId={metaListingCategoryId}
                  saving={savingMeta}
                  onIconChange={setMetaIcon}
                  onRemarkChange={setMetaRemark}
                  onListingCategoryChange={setMetaListingCategoryId}
                  onSave={handleSaveMeta}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Dialogs ── */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} title={t('reset_to_defaults')}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">{t('confirm_reset')}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setResetDialogOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleReset}>{t('reset_to_defaults')}</Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        title={config?.isActive ? 'Deactivate Config' : 'Activate Config'}
      >
        <div className="space-y-4 min-w-[360px]">
          {config?.isActive ? (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Deactivating <strong>{label}</strong> will hide it from the mobile app immediately.
              </p>
              <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
                Users will no longer be able to create new listings of this type until reactivated.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Activating <strong>{label}</strong> will make it visible to the mobile app again.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button variant={config?.isActive ? 'danger' : 'primary'} onClick={handleToggleStatus}>
              {config?.isActive ? 'Yes, Deactivate' : 'Yes, Activate'}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        title="Delete Config Permanently"
      >
        <div className="space-y-4 min-w-[400px]">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm font-semibold text-red-700 dark:text-red-400">This action cannot be undone.</p>
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">
              Deleting <strong>{label}</strong> will permanently remove all fields, options, steps, and translation data.
              The mobile app will fail to load forms of this type.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Type <strong>{formType}</strong> to confirm
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:outline-none"
              placeholder={formType}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={deleting}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteConfirmText !== formType || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete Permanently'}
            </Button>
          </div>
        </div>
      </Dialog>

      <AddFieldDialog
        open={addFieldOpen}
        allSteps={addFieldAllSteps}
        allGroups={allGroups}
        defaultStep={addFieldDefaultStep}
        existingKeys={existingKeys}
        onClose={() => setAddFieldOpen(false)}
        onAdd={handleAddField}
      />
      {ConfirmDialog}
    </Layout>
  );
};

export default FormConfigEditor;
